/**
 * Copy rich content to the clipboard and open a blank Google Doc.
 *
 * Browsers cannot paste into Google Docs automatically. This helper copies
 * formatted HTML (when available) before opening a new tab, because window.open
 * steals focus and blocks clipboard writes on unfocused pages.
 */

export function getPasteShortcut(
  userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "",
): string {
  return /Mac|iPod|iPhone|iPad/.test(userAgent) ? "⌘V" : "Ctrl+V";
}

export function buildGoogleDocCreateUrl(title: string): string {
  return `https://docs.google.com/document/create?title=${encodeURIComponent(title)}`;
}

export async function copyRichToClipboard(options: {
  html?: string;
  plain: string;
}): Promise<{ ok: boolean }> {
  const { html, plain } = options;
  try {
    if (html && typeof ClipboardItem !== "undefined") {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return { ok: true };
    }
    await navigator.clipboard.writeText(plain);
    return { ok: true };
  } catch {
    try {
      await navigator.clipboard.writeText(plain);
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }
}

export type CopyAndOpenGoogleDocOptions = {
  html?: string;
  plain: string;
  docTitle: string;
  openDelayMs?: number;
};

export async function copyAndOpenGoogleDoc(
  options: CopyAndOpenGoogleDocOptions,
): Promise<{ ok: boolean }> {
  const { html, plain, docTitle, openDelayMs = 400 } = options;
  const copyResult = await copyRichToClipboard({ html, plain });
  if (!copyResult.ok) {
    return { ok: false };
  }

  const url = buildGoogleDocCreateUrl(docTitle);
  window.setTimeout(() => {
    window.open(url, "_blank");
  }, openDelayMs);

  return { ok: true };
}
